import dayjs from 'dayjs';
import { useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { backend } from '../axios/backend';
import DeleteModal from '../components/DeleteModal';
import PrettyLoader from '../components/PrettyLoader';
import ProductComponent from '../components/ProductComponent';
import CakeMini from '../components/icons/CakeMini';
import DeleteMini from '../components/icons/DeleteMini';
import PencilMini from '../components/icons/PencilMini';
import Plus from '../components/icons/Plus';
import ShareMini from '../components/icons/ShareMini';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import Button from '../components/ui/Button';
import IconButton from '../components/ui/IconButton';
import Input from '../components/ui/Input';
import { useAppContext } from '../context/AppContext';
import { useDeleteProduct } from '../hooks/useDeleteProduct';
import { useIdeas } from '../hooks/useIdeas';
import { parseErrorMessage } from '../utils/error-message';

export type Idea = {
  id: string;
  name: string;
  price: number;
  comment: string;
  url: string;
  imageUrl?: string;
};

const IdeasForPersonPage = () => {
  const { personId } = useParams();
  const navigate = useNavigate();
  const { alertSuccess, alertError } = useAppContext();

  const [toDelete, setToDelete] = useState<Idea | undefined>(undefined);
  const [toDeletePerson, setToDeletePerson] = useState<boolean | undefined>(false);

  const query = useIdeas(personId || '');

  const deleteMutation = useDeleteProduct(personId, { onSuccess: () => setToDelete(undefined) });

  const deletePersonMutation = useMutation((personId: string) => backend.delete(`person/${personId}`), {
    onError: (e) => {
      alertError(parseErrorMessage(e));
    },
    onSuccess: () => {
      alertSuccess(`${query.data?.person.name} erfolgreich gelöscht.`);
      navigate('/ideas');
    },
  });

  return (
    <div style={{ margin: '2rem' }} className="space-y-4">
      <Breadcrumbs
        crumbs={[
          { text: 'Ideen', route: '/ideas' },
          { text: query.data?.person.name || 'Person', route: '/ideas/' + personId },
        ]}
      />
      <PrettyLoader useQueryResult={query}>
        <div className="flex flex-col md:flex-row md:justify-between md:align-center gap-4">
          <div className="space-y-1">
            <h1 className="text-white text-xl">
              <b>Ideen für {query.data?.person.name}</b>
            </h1>
            <p className="text-white text-sm">
              {query.data?.person.dateOfBirth ? (
                <div className="flex flex-row space-x-2">
                  <CakeMini />
                  <span>{dayjs(query.data?.person.dateOfBirth).format('DD.MM.YYYY')}</span>
                </div>
              ) : (
                ''
              )}
            </p>
          </div>
          <div className="flex flex-wrap flex-row gap-2 justify-end items-center">
            <IconButton Icon={Plus} onClick={() => navigate(`/ideas/${personId}/add`)} />
            <IconButton Icon={PencilMini} onClick={() => navigate(`/ideas/${personId}/edit`)} />
            <IconButton Icon={ShareMini} onClick={() => navigate(`/ideas/${personId}/share`)} />
            <IconButton Icon={DeleteMini} onClick={() => setToDeletePerson(true)} variant="danger" />
          </div>
        </div>

        <ul className="divide-y divide-gray-400 mb-4">
          {query.data?.ideas.map((idea) => (
            <ProductComponent
              product={idea}
              key={idea.id}
              onDelete={() => setToDelete(idea)}
              onEdit={() => navigate(`/ideas/${personId}/${idea.id}/edit`)}
            />
          ))}
        </ul>
        <div className="flex flex-row space-x-4">
          <Input name='add-idea' value='' onChange={() => { }} placeholder='Was hast du für eine Idee?' onClick={() => navigate(`/ideas/${personId}/add`)} />
          <Button onClick={() => navigate(`/ideas/${personId}/add`)} Icon={Plus}>
            Idee
          </Button>
        </div>
      </PrettyLoader>
      <DeleteModal
        toDelete={toDelete}
        setToDelete={setToDelete}
        isLoading={deleteMutation.isLoading}
        onDelete={() => toDelete && deleteMutation.mutate(toDelete.id)}
        what={`diese Idee (${toDelete?.name})`}
      />
      <DeleteModal
        toDelete={toDeletePerson}
        setToDelete={setToDeletePerson}
        onDelete={() => personId && deletePersonMutation.mutate(personId)}
        isLoading={deletePersonMutation.isLoading}
        what={`diese Person (${query.data?.person.name})`}
      />
    </div>
  );
};

export default IdeasForPersonPage;
